import {
  type MediaCardType,
  mediaCardTypeToKeys,
} from '@wandb/weave/common/types/media';

import {useRampFlagMediaSettingsDrawerAllTypesUser} from '../../util/rampFeatureFlags/rampFeatureFlags';

type MediaDrawerTypes = {
  types: MediaCardType[];
  /**
   * The wire values behind `types`.
   *
   * `mediaStrings` covers every media type the app knows, including ones no
   * media card renders. This is the subset a supported panel could actually
   * hold, so it is what the drawer's queries filter on.
   */
  typeStrings: string[];
};

const makeMediaDrawerTypes = (types: MediaCardType[]): MediaDrawerTypes => ({
  types,
  typeStrings: types.flatMap(mediaCardTypeToKeys) as string[],
});

/**
 * Media card types the media settings drawer renders settings for when
 * `media_settings_drawer_all_types_user` is off. Panels of any other card type
 * (html, plotly, bokeh, molecule, table) use the settings popup instead.
 */
export const DEFAULT_MEDIA_DRAWER_TYPES = makeMediaDrawerTypes([
  'image',
  'video',
  'audio',
  'object3D',
]);

/** Every media card type, used when `media_settings_drawer_all_types_user` is on. */
export const ALL_MEDIA_DRAWER_TYPES = makeMediaDrawerTypes([
  ...DEFAULT_MEDIA_DRAWER_TYPES.types,
  'plotly',
  'molecule',
  'html',
  'table',
  'bokeh',
]);

/**
 * The media card types the drawer supports for this user. The drawer's entry
 * points gate on these and its metric search filters to them.
 */
export const useSupportedMediaDrawerTypes = (): MediaDrawerTypes =>
  useRampFlagMediaSettingsDrawerAllTypesUser()
    ? ALL_MEDIA_DRAWER_TYPES
    : DEFAULT_MEDIA_DRAWER_TYPES;
